import { useState, useEffect, useRef } from 'react';

/**
 * A hook that delays showing a loading skeleton by `delayMs`.
 * If the skeleton is shown, it ensures it stays visible for at least `minDurationMs`
 * to prevent flickering/glitching when the API resolves right after the delay.
 */
export function useDelayedSkeleton(
  isFetching,
  delayMs = 300,
  minDurationMs = 500,
) {
  const [showSkeleton, setShowSkeleton] = useState(false);
  const skeletonStartTimeRef = useRef(0);

  useEffect(() => {
    let delayTimer;
    let minDurationTimer;

    if (isFetching) {
      if (!showSkeleton) {
        // Start the delay timer to show the skeleton
        delayTimer = setTimeout(() => {
          setShowSkeleton(true);
          skeletonStartTimeRef.current = Date.now();
        }, delayMs);
      }
    } else {
      if (showSkeleton) {
        // If it's already showing, ensure it stays for at least minDurationMs
        const timeShown = Date.now() - skeletonStartTimeRef.current;
        const timeRemaining = minDurationMs - timeShown;

        if (timeRemaining > 0) {
          minDurationTimer = setTimeout(() => {
            setShowSkeleton(false);
          }, timeRemaining);
        } else {
          setShowSkeleton(false);
        }
      }
    }

    return () => {
      clearTimeout(delayTimer);
      clearTimeout(minDurationTimer);
    };
  }, [isFetching, showSkeleton, delayMs, minDurationMs]);

  return showSkeleton;
}
